/**
 * PDP description anchors + smooth scrolling for the buy-box anchor tabs.
 *
 * Migrated product descriptions carry their own "Suggested Use" and
 * "Ingredients" copy under free-form headings whose wording varies a lot
 * ("How To Use", "Directions", "Suggested Use:", "What is <product>"…). The
 * metafield-driven info columns (#directions / #ingredients) are mostly empty,
 * so instead of leaving those anchor icons pointing at nothing, this finds the
 * matching heading inside the description and gives it the id the anchor already
 * targets. When nothing matches — and no metafield column rendered that id — the
 * dead anchor is hidden.
 *
 * Every anchor tab (#description, #reviews, #related-products, #ingredients,
 * #directions) also gets smooth scrolling, honouring the target's
 * scroll-margin-top and the user's reduced-motion preference.
 */
if (!customElements.get('ws-product-description')) {
  const HEADINGS = 'h1, h2, h3, h4, h5, h6, strong, b';
  const BLOCKS = 'span, p, div, li';

  const ANCHORS = [
    {
      // The "Suggested Use" buy-box anchor is wired to #directions.
      id: 'directions',
      keywords: [
        'suggested use', 'recommended use', 'recommended usage',
        'how to use', 'how to take', 'directions', 'dosage', 'usage',
      ],
    },
    {
      id: 'ingredients',
      keywords: [
        'ingredients', 'supplement facts', 'nutrition facts',
        "what's inside", 'what is inside',
      ],
      // Fall back to a "What is <product>" style intro heading.
      fallback: ['what is', 'what it is'],
    },
  ];

  class WsProductDescription extends HTMLElement {
    #abort = null;

    connectedCallback() {
      this.#abort = new AbortController();

      const content = this.querySelector('.ws-product-description__content');
      if (content) this.#mapAnchors(content);

      // Wire smooth scroll after mapping so dynamically-assigned ids resolve.
      this.#wireSmoothScroll();
    }

    disconnectedCallback() {
      this.#abort?.abort();
    }

    #mapAnchors(content) {
      const headings = [...content.querySelectorAll(HEADINGS)];
      const blocks = [...content.querySelectorAll(BLOCKS)];
      const used = new Set();

      for (const cfg of ANCHORS) {
        // A populated metafield column may already own this id — never duplicate.
        if (document.getElementById(cfg.id)) continue;

        const target =
          this.#match(headings, cfg.keywords, used, false) ||
          this.#match(blocks, cfg.keywords, used, true) ||
          (cfg.fallback &&
            (this.#match(headings, cfg.fallback, used, false) ||
              this.#match(blocks, cfg.fallback, used, true)));

        if (target) {
          target.id = cfg.id;
          target.classList.add('ws-product-description__anchor-target');
          used.add(target);
        } else {
          this.#hideAnchor(cfg.id);
        }
      }
    }

    /**
     * Short heading-like elements are matched anywhere in their text; free-form
     * blocks must START with the keyword so a mid-sentence mention can't hijack
     * the anchor.
     */
    #match(elements, keywords, used, atStart) {
      for (const keyword of keywords) {
        for (const el of elements) {
          if (used.has(el) || el.id) continue;
          const text = (el.textContent || '').trim().toLowerCase();
          if (!text) continue;
          if (atStart ? text.startsWith(keyword) : text.includes(keyword)) {
            return el;
          }
        }
      }
      return null;
    }

    #hideAnchor(id) {
      // .ws-product__anchor is display:flex, so hide via inline style, not [hidden].
      const link = document.querySelector(`.ws-product__anchor[href="#${id}"]`);
      if (link) link.style.display = 'none';
    }

    #wireSmoothScroll() {
      const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
      const { signal } = this.#abort;

      for (const link of document.querySelectorAll('.ws-product__anchor')) {
        link.addEventListener(
          'click',
          (event) => {
            const hash = link.getAttribute('href') || '';
            if (hash[0] !== '#') return;
            const target = document.getElementById(hash.slice(1));
            if (!target) return;
            event.preventDefault();
            target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
            history.replaceState(null, '', hash);
          },
          { signal }
        );
      }
    }
  }

  customElements.define('ws-product-description', WsProductDescription);
}
